import { useCleanMessages } from '../hooks/store/useMessageHooks';
import { MessageDisclaimer } from './MessageDisclaimer';
import { MessageInput } from './message-input/MessageInput';
import { Box, List } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageItem } from './message-items/MessageItem';
import { LoadingIndicator } from '@xolv-mybrightlink/common';
import { useCurrentUserGuid } from '../../../firebase/useCollection';
import { useMessageLoader } from '../hooks/firebase/useMessageLoader';
import { useMessageListener } from '../hooks/firebase/useMessageListener';
import { useReadPosition } from '../hooks/store/useReadPosition';
import { useMarkRead } from '../hooks/firebase/useMarkRead';
import { Message } from '../type';
import { MessageDivider } from './MessageDivider';
import { useChannel } from '../hooks/store/useChannelHooks';
import { useUnreadCount } from '../hooks/store/useUnreadCount';
import { NewMessageIndicator } from './NewMessageIndicator';
import { useUploadingMessages } from '../hooks/store/useUploadingMessages';
import { useIntersectionObserver } from '@xolv-mybrightlink/common';

export const Messaging = (props: {
  channelGuid: string;
  header: React.ReactNode;
  standalone?: boolean;
}) => {
  const { channelGuid, standalone } = props;
  const channel = useChannel(channelGuid);
  const currentUserGuid = useCurrentUserGuid();
  const readMessageGuid = useReadPosition(channelGuid, currentUserGuid);
  const remoteMessages = useCleanMessages(channelGuid);
  const uploadingMessages = useUploadingMessages(channelGuid);
  const unreadCount = useUnreadCount({ type: 'channel', guid: channelGuid });
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadMessages = useMessageLoader(channelGuid);
  const firstUnreadMessageRef = useRef<HTMLLIElement>(null);
  const markRead = useMarkRead(channelGuid);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreMessages = useCallback(async () => {
    setIsLoading(true);
    await loadMessages();
    setIsLoading(false);
  }, [loadMessages]);
  useMessageListener(channelGuid);
  useIntersectionObserver(loadMoreRef, loadMoreMessages);
  useIntersectionObserver(firstUnreadMessageRef, markRead);

  const scrollToFirstUnreadMessage = useCallback(() => {
    if (firstUnreadMessageRef.current) {
      firstUnreadMessageRef.current.scrollIntoView({ behavior: 'smooth' });
      return true;
    }
    return false;
  }, [firstUnreadMessageRef]);

  // scroll to first unread message when the page have finished loading
  // expected to run once
  const [scrolledToUnreadMessage, setScrolledToUnreadMessage] = useState(
    unreadCount > 0,
  );
  useEffect(() => {
    if (scrolledToUnreadMessage) {
      setScrolledToUnreadMessage(!scrollToFirstUnreadMessage());
    }
  }, [scrollToFirstUnreadMessage, scrolledToUnreadMessage]);

  if (!remoteMessages || !channel) {
    return <LoadingIndicator ref={loadMoreRef} fullHeight />;
  }

  const firstUnreadMessage = findFistUnreadMessage(
    remoteMessages,
    readMessageGuid,
  );

  const messages = mergeMessages(uploadingMessages, remoteMessages);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'flex-end',
      }}
    >
      {props.header}
      <MessageDisclaimer
        channelType={channel.type}
        readonly={channel.readonly}
      />
      <List
        className='hide-scrollbar'
        sx={{
          display: 'flex',
          flexDirection: 'column-reverse',
          flex: 1,
          px: standalone ? 2.5 : 5,
          py: standalone ? 1.25 : 2,
          overflow: 'auto',
        }}
      >
        {messages.map((message, index, self) => {
          const isMyMessage = message.authorGuid === currentUserGuid;
          const messageRef =
            message.guid === firstUnreadMessage?.guid
              ? firstUnreadMessageRef
              : null;
          return (
            <React.Fragment key={message.guid}>
              <MessageItem
                ref={messageRef}
                myMessage={isMyMessage}
                message={message}
                standalone={standalone}
              />
              <MessageDivider
                message={message}
                previousMessage={self[index + 1]}
              />
            </React.Fragment>
          );
        })}
        <Box sx={{ top: 400, position: 'relative' }} ref={loadMoreRef} />
        {isLoading && (
          <Box sx={{ p: 4 }}>
            <LoadingIndicator fullHeight />
          </Box>
        )}
        {unreadCount > 0 && (
          <NewMessageIndicator onClick={scrollToFirstUnreadMessage} />
        )}
      </List>
      <MessageInput channelGuid={channelGuid} standalone={standalone} />
    </Box>
  );
};

const findFistUnreadMessage = (
  messages: Message[] | null,
  readMessageGuid: string | null,
): Message | null => {
  if (!messages?.length) {
    return null;
  }
  if (messages[0].guid === readMessageGuid) {
    return messages[0];
  }
  return (
    messages.find((_, index) => {
      return (
        index < messages.length - 1 &&
        messages[index + 1].guid === readMessageGuid
      );
    }) ?? messages[0]
  );
};

const mergeMessages = (
  uploadingMessages: Message[],
  remoteMessages: Message[],
) => {
  const copiedRemoteMessages = [...remoteMessages];
  const copiedUploadingMessages = [...uploadingMessages];
  copiedRemoteMessages.forEach((remote, index) => {
    const uploadingIndex = copiedUploadingMessages.findIndex(
      (u) => u.guid === remote.guid,
    );
    if (uploadingIndex !== -1) {
      if (remote.scanStatus === 'NOT_STARTED') {
        const uploading = copiedUploadingMessages[uploadingIndex];
        copiedRemoteMessages[index] = {
          ...remote,
          url: uploading.url,
          fileName: uploading.fileName,
        };
      }
      copiedUploadingMessages.splice(uploadingIndex, 1);
    }
  });
  return [...copiedUploadingMessages, ...copiedRemoteMessages].sort(
    (m1, m2) => m2.createdDate.toMillis() - m1.createdDate.toMillis(),
  );
};
